import React, { useEffect, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { withRouter } from 'hooks/withRouter'
import { apiRequest } from 'store/Misc/apiRequest'
import BreadCrumb from 'components/BreadCrumb'
import { getMockCampaignById } from '../../mock/campaigns'
import { normalizeStatus } from '../../utils/campaignStatus'
import DetailHeader from './DetailHeader'
import OverviewTab from './tabs/OverviewTab'
import PerformanceTab from './tabs/PerformanceTab'
import AudienceTab from './tabs/AudienceTab'
import LocationTab from './tabs/LocationTab'
import DevicesTab from './tabs/DevicesTab'
import TargetingScheduleTab from './tabs/TargetingScheduleTab'
import CreativeRepeatTab from './tabs/CreativeRepeatTab'

const TABS = [
  { key: 'overview', labelKey: 'rewardAds.detail.tabs.overview', icon: UIcons.UilPlayCircle, Component: OverviewTab },
  { key: 'performance', labelKey: 'rewardAds.detail.tabs.performance', icon: UIcons.UilChartLine, Component: PerformanceTab },
  { key: 'audience', labelKey: 'rewardAds.detail.tabs.audience', icon: UIcons.UilUsersAlt, Component: AudienceTab },
  { key: 'location', labelKey: 'rewardAds.detail.tabs.location', icon: UIcons.UilLocationPinAlt, Component: LocationTab },
  { key: 'devices', labelKey: 'rewardAds.detail.tabs.devices', icon: UIcons.UilDesktop, Component: DevicesTab },
  { key: 'targeting_schedule', labelKey: 'rewardAds.detail.tabs.targeting_schedule', icon: UIcons.UilCalendarAlt, Component: TargetingScheduleTab },
  { key: 'creative_repeat', labelKey: 'rewardAds.detail.tabs.creative_repeat', icon: UIcons.UilSync, Component: CreativeRepeatTab }
]

function formatScheduleDays(schedules) {
  if (!schedules?.length) return null
  return [...new Set(schedules.map((schedule) => schedule.day))].join(', ')
}

function formatScheduleHours(schedules) {
  if (!schedules?.length) return null
  return schedules.map((schedule) => `${schedule.from?.name_12} - ${schedule.to?.name_12}`).join(', ')
}

function capitalize(value) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function formatAgeRange(minAge, maxAge) {
  if (minAge && maxAge) return `${minAge} - ${maxAge} years`
  if (minAge) return `${minAge}+ years`
  if (maxAge) return `Up to ${maxAge} years`
  return 'All ages'
}

// promos/campaigns/{id} only carries title/description/CTA/callback/status/budget/
// dates/frequency/schedules/impressions/views/clicks — the rest of this design
// (funnels, audience/location/device breakdowns, cohort tables, campaign reach
// estimates) has no backing endpoint yet, so it stays on the demo mock scaffold
// (matching the rest of this feature's "not yet wired to a backend" convention).
function buildDetailCampaign(id, real) {
  const mock = getMockCampaignById(id)
  if (!real) return mock

  const daysLeft = real.end ? Math.max(0, dayjs(real.end).diff(dayjs(), 'day')) : mock.period.daysLeft
  const periodLabel = real.start && real.end
    ? `${dayjs(real.start).format('MMM D')} - ${dayjs(real.end).format('MMM D, YYYY')}`
    : mock.period.label
  const durationDays = real.start && real.end ? Math.max(1, dayjs(real.end).diff(dayjs(real.start), 'day') + 1) : null
  const dailyBudget = real.budget && durationDays ? Math.round(real.budget / durationDays) : mock.dailyBudget

  const performanceStats = mock.performance.stats.map((stat) => {
    if (stat.key === 'impressions' && real.impressions != null) return { ...stat, value: real.impressions.toLocaleString(), trend: undefined }
    if (stat.key === 'view_completed' && real.views_count != null) return { ...stat, value: real.views_count.toLocaleString(), trend: undefined }
    if (stat.key === 'cta_clicks' && real.clicks_count != null) return { ...stat, value: real.clicks_count.toLocaleString(), trend: undefined }
    return stat
  })

  const audienceStats = mock.audience.stats.map((stat) => (
    stat.key === 'unique_viewers' && real.views_count != null
      ? { ...stat, value: real.views_count.toLocaleString(), trend: undefined }
      : stat
  ))

  const assetStatus = real.blocked
    ? 'Blocked'
    : capitalize(real.status ?? 'active')

  return {
    ...mock,
    id: real.id,
    title: real.title ?? mock.title,
    description: real.description ?? mock.description,
    ctaMessage: real.message ?? mock.ctaMessage,
    callbackUrl: real.callback_url ?? mock.callbackUrl,
    status: normalizeStatus(real.status),
    blocked: real.blocked ?? false,
    deleteRequestedAt: real.delete_requested_at ?? null,
    assetStatus,
    budget: real.budget ?? mock.budget,
    dailyBudget,
    campaignId: `#${real.id}`,
    thumbnailUrl: real.thumbnail_stream,
    mediaUrl: real.media,
    realTargeting: {
      gender: real.gender && real.gender !== 'all' ? capitalize(real.gender) : 'All genders',
      maritalStatus: real.marital_status && real.marital_status !== 'all' ? capitalize(real.marital_status) : 'All marital statuses',
      ageRange: formatAgeRange(real.min_age, real.max_age),
      location: real.lat && real.lng ? `${real.lat}, ${real.lng}` : null
    },
    period: {
      ...mock.period,
      start: real.start ? dayjs(real.start).format('MMM D, YYYY') : mock.period.start,
      end: real.end ? dayjs(real.end).format('MMM D, YYYY') : mock.period.end,
      label: periodLabel,
      daysLeft
    },
    video: {
      ...mock.video,
      name: real.title ?? mock.video.name,
      format: real.type ? real.type.toUpperCase() : mock.video.format,
      duration: real.duration ?? mock.video.duration
    },
    scheduleSummary: {
      ...mock.scheduleSummary,
      startDate: real.start ? dayjs(real.start).format('MMM D YYYY') : mock.scheduleSummary.startDate,
      endDate: real.end ? dayjs(real.end).format('MMM D YYYY') : mock.scheduleSummary.endDate,
      daysToDisplay: formatScheduleDays(real.schedules) ?? mock.scheduleSummary.daysToDisplay,
      hoursToDisplay: formatScheduleHours(real.schedules) ?? mock.scheduleSummary.hoursToDisplay,
      frequency: real.frequency?.name ?? mock.scheduleSummary.frequency
    },
    performance: { ...mock.performance, stats: performanceStats },
    audience: { ...mock.audience, stats: audienceStats }
  }
}

export default withTranslation()(withRouter(function RewardAdDetail({ t, router: { params, navigate } }) {
  const { data, isValidating } = useSWR(`promos/campaigns/${params.id}`)
  const real = data?.data

  const campaign = useMemo(() => buildDetailCampaign(params.id, real), [params.id, real])
  const [activeTab, setActiveTab] = useState('overview')
  const [dailyViews, setDailyViews] = useState([])

  // Same endpoint/payload shape as v1's /promo/campaign/monitoring?id={id} page
  // (Monitoring/index.js): POST promos/campaigns/views-by-date-range with
  // { promo_campaign_id, start_date, end_date }, response shaped { views: { 'YYYY-MM-DD': count } }.
  useEffect(() => {
    if (!real?.id || !real.start || !real.end) return

    let cancelled = false

    apiRequest({
      method: 'post',
      url: 'promos/campaigns/views-by-date-range',
      payload: {
        promo_campaign_id: real.id,
        start_date: dayjs(real.start).format('YYYY-MM-DD'),
        end_date: dayjs(real.end).format('YYYY-MM-DD')
      }
    }).then((response) => {
      if (cancelled) return
      const views = response.data?.views ?? {}
      setDailyViews(Object.entries(views).map(([date, value]) => ({ label: dayjs(date).format('MMM D'), value })))
    }).catch(() => {})

    return () => { cancelled = true }
  }, [real?.id, real?.start, real?.end])

  const ActiveTabComponent = TABS.find((tab) => tab.key === activeTab)?.Component ?? OverviewTab
  const notReady = () => toast.info('This action is coming soon')

  if (isValidating && !data) {
    return (
      <section className="w-full max-w-6xl mx-auto pb-10">
        <p className="text-sm text-secondary py-10 text-center">Loading reward ad...</p>
      </section>
    )
  }

  return (
    <section className="w-full max-w-6xl mx-auto pb-10">
      <BreadCrumb
        items={[
          { name: t('rewardAds.title'), onClick: () => navigate('/reward-ads') },
          { name: t('rewardAds.detail.breadcrumb') }
        ]}
      />

      <DetailHeader
        campaign={campaign}
        onEdit={notReady}
        onDuplicate={notReady}
        onEndCampaign={notReady}
      />

      <div className="flex items-center gap-1 border-b border-border-lines mb-4 overflow-x-auto">
        {
          TABS.map(({ key, labelKey, icon: Icon }) => {
            const isActive = key === activeTab
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${
                  isActive ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-dark-blue'
                }`}
              >
                <Icon size={16} /> {t(labelKey)}
              </button>
            )
          })
        }
      </div>

      <ActiveTabComponent campaign={campaign} dailyViews={dailyViews} />
    </section>
  )
}))
